/**
 * Our scrollbars: a square, black bar that floats over the content, flush with the right edge, the
 * same on Mac and Windows. With a mouse only; touch screens keep their own (already floating) bars.
 *
 * - The page: its scroller is .page-wrapper from 990px up (base.css), the document below that. Its
 *   native bar is hidden (CSS on html[data-page-scrollbar], custom-layout.css), so the page uses the
 *   full window width: no gutter on Windows. Its bar is fixed to the window's right edge.
 * - Inner scroll areas (INNER: the quick add dialog's details column) get the same bar, placed over
 *   their own right edge inside their offset parent. Photo galleries and their thumbnail rail never
 *   show a bar (their native one is hidden by the theme).
 * - A bar shows while its area scrolls and while the pointer is near that right edge, then fades.
 *   Drag the thumb to scroll; a click on the track jumps there. Wheel, trackpad and keys are the
 *   browser's own scrolling, untouched.
 * - The page's bar hides while a drawer or dialog locks the page (html[scroll-lock]); any bar hides
 *   when its area doesn't scroll.
 */

const mouse = window.matchMedia('(hover: hover) and (pointer: fine)');
const EDGE = 16;
const IDLE_MS = 1000;
const MIN_THUMB = 40;
const INNER = '.quick-add-modal__content .product-details';

const root = document.documentElement;

function pageScroller() {
  const wrapper = /** @type {HTMLElement | null} */ (document.querySelector('.page-wrapper'));
  if (wrapper && /auto|scroll/.test(getComputedStyle(wrapper).overflowY)) return wrapper;
  return /** @type {HTMLElement} */ (document.scrollingElement || root);
}

class OverlayScrollbar {
  /**
   * @param {HTMLElement} scroller
   * @param {boolean} inner - An area inside the page, rather than the page itself.
   */
  constructor(scroller, inner) {
    this.scroller = scroller;
    this.inner = inner;
    this.bar = document.createElement('div');
    this.bar.className = `page-scrollbar page-scrollbar--${inner ? 'inner' : 'page'}`;
    this.bar.setAttribute('aria-hidden', 'true');
    this.thumb = document.createElement('div');
    this.thumb.className = 'page-scrollbar__thumb';
    this.bar.append(this.thumb);
    this.thumbHeight = 0;
    this.travel = 0;
    this.frame = 0;
    /** @type {ReturnType<typeof setTimeout> | undefined} */
    this.idleTimer = undefined;
    /** @type {{ y: number, top: number } | null} */
    this.drag = null;
    this.near = false;
    // Content that grows or shrinks (images loading, accordions) moves the thumb.
    this.resizes = new ResizeObserver(() => this.schedule());
    this.#bind();
  }

  mount() {
    const host = this.inner ? this.scroller.offsetParent : document.body;
    if (host && this.bar.parentElement !== host) host.append(this.bar);
    this.resizes.disconnect();
    this.resizes.observe(this.scroller);
    for (const child of this.scroller === root ? [document.body] : this.scroller.children) this.resizes.observe(child);
    this.schedule();
  }

  unmount() {
    this.bar.remove();
    this.resizes.disconnect();
  }

  /** @param {HTMLElement} scroller */
  setScroller(scroller) {
    if (scroller === this.scroller) return;
    this.scroller = scroller;
    this.mount();
  }

  schedule() {
    if (!this.frame) this.frame = requestAnimationFrame(() => this.#update());
  }

  /** Shows the bar, and fades it after a moment unless the pointer is on it or dragging. */
  wake() {
    this.bar.setAttribute('data-active', '');
    clearTimeout(this.idleTimer);
    this.idleTimer = setTimeout(() => {
      if (!this.drag && !this.near) this.bar.removeAttribute('data-active');
    }, IDLE_MS);
  }

  /** @param {PointerEvent} event */
  pointer(event) {
    let near;
    if (this.inner) {
      const rect = this.scroller.getBoundingClientRect();
      near =
        event.clientX <= rect.right &&
        event.clientX >= rect.right - EDGE &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;
    } else {
      near = event.clientX >= window.innerWidth - EDGE;
    }
    this.setNear(near);
  }

  /** @param {boolean} near */
  setNear(near) {
    if (near === this.near) return;
    this.near = near;
    this.bar.toggleAttribute('data-near', near);
    if (near) this.wake();
    else if (!this.drag) this.wake();
  }

  #update() {
    this.frame = 0;
    const { scroller, bar, thumb } = this;
    const view = scroller.clientHeight;
    const total = scroller.scrollHeight;
    const scrollable = scroller.isConnected && total - view > 1;
    bar.toggleAttribute('hidden', !scrollable);
    if (!scrollable) return;
    if (this.inner) this.#place();
    const height = bar.clientHeight;
    this.thumbHeight = Math.max(MIN_THUMB, Math.round((height * view) / total));
    this.travel = height - this.thumbHeight;
    const ratio = scroller.scrollTop / (total - view);
    thumb.style.height = `${this.thumbHeight}px`;
    thumb.style.translate = `0 ${Math.round(ratio * this.travel)}px`;
  }

  /** An inner bar covers the right edge of its area, in its offset parent's coordinates. */
  #place() {
    const host = /** @type {HTMLElement | null} */ (this.bar.parentElement);
    if (!host) return;
    const area = this.scroller.getBoundingClientRect();
    const box = host.getBoundingClientRect();
    const top = area.top - box.top - host.clientTop + host.scrollTop;
    const right = area.right - box.left - host.clientLeft + host.scrollLeft;
    this.bar.style.top = `${top}px`;
    this.bar.style.left = `${right - this.bar.offsetWidth}px`;
    this.bar.style.height = `${area.height}px`;
  }

  /** @param {number} top */
  #scrollTo(top, behavior = /** @type {ScrollBehavior} */ ('instant')) {
    this.scroller.scrollTo({ top, behavior });
  }

  /** How far the area scrolls per pixel of thumb travel. */
  #perPixel() {
    return this.travel > 0 ? (this.scroller.scrollHeight - this.scroller.clientHeight) / this.travel : 0;
  }

  #bind() {
    const { bar, thumb } = this;

    thumb.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();
      thumb.setPointerCapture(event.pointerId);
      this.drag = { y: event.clientY, top: this.scroller.scrollTop };
      bar.setAttribute('data-dragging', '');
      this.wake();
    });

    thumb.addEventListener('pointermove', (event) => {
      if (!this.drag) return;
      this.#scrollTo(this.drag.top + (event.clientY - this.drag.y) * this.#perPixel());
    });

    /** @param {PointerEvent} event */
    const endDrag = (event) => {
      if (!this.drag) return;
      this.drag = null;
      bar.removeAttribute('data-dragging');
      if (thumb.hasPointerCapture(event.pointerId)) thumb.releasePointerCapture(event.pointerId);
      this.wake();
    };
    thumb.addEventListener('pointerup', endDrag);
    thumb.addEventListener('pointercancel', endDrag);

    // A click on the track: the thumb's middle goes there. Not a click inside the dialog's content.
    bar.addEventListener('pointerdown', (event) => {
      if (event.button !== 0 || event.target === thumb) return;
      event.preventDefault();
      event.stopPropagation();
      const y = event.clientY - bar.getBoundingClientRect().top - this.thumbHeight / 2;
      this.#scrollTo(Math.max(0, Math.min(this.travel, y)) * this.#perPixel(), 'smooth');
    });
  }
}

const page = new OverlayScrollbar(pageScroller(), false);
/** @type {Map<HTMLElement, OverlayScrollbar>} */
const inner = new Map();

/** Inner areas come and go (the quick add dialog is filled per product). */
function syncInner() {
  for (const [scroller, scrollbar] of inner) {
    if (!scroller.isConnected) {
      scrollbar.unmount();
      inner.delete(scroller);
    } else if (!scrollbar.bar.isConnected) {
      scrollbar.mount();
    }
  }
  for (const scroller of /** @type {NodeListOf<HTMLElement>} */ (document.querySelectorAll(INNER))) {
    if (inner.has(scroller)) continue;
    const scrollbar = new OverlayScrollbar(scroller, true);
    inner.set(scroller, scrollbar);
    scrollbar.mount();
  }
}

let syncQueued = false;
const additions = new MutationObserver(() => {
  if (syncQueued) return;
  syncQueued = true;
  requestAnimationFrame(() => {
    syncQueued = false;
    syncInner();
  });
});

/** @param {Event} event */
function onScroll(event) {
  const target = event.target === document ? document.scrollingElement : event.target;
  if (target === page.scroller) {
    page.schedule();
    page.wake();
    return;
  }
  const scrollbar = inner.get(/** @type {HTMLElement} */ (target));
  if (scrollbar) {
    scrollbar.schedule();
    scrollbar.wake();
    return;
  }
  // Something else scrolled (the dialog itself, a drawer): inner bars may have moved.
  for (const other of inner.values()) other.schedule();
}

/** @param {PointerEvent} event */
function onPointerMove(event) {
  if (event.pointerType !== 'mouse') return;
  page.pointer(event);
  for (const scrollbar of inner.values()) scrollbar.pointer(event);
}

function onPointerLeave() {
  page.setNear(false);
  for (const scrollbar of inner.values()) scrollbar.setNear(false);
}

function onResize() {
  page.setScroller(pageScroller());
  page.schedule();
  for (const scrollbar of inner.values()) scrollbar.schedule();
}

function enable() {
  root.setAttribute('data-page-scrollbar', '');
  page.mount();
  syncInner();
  additions.observe(document.body, { childList: true, subtree: true });
  document.addEventListener('scroll', onScroll, { capture: true, passive: true });
  document.addEventListener('pointermove', onPointerMove, { passive: true });
  document.addEventListener('pointerleave', onPointerLeave, { passive: true });
  window.addEventListener('resize', onResize, { passive: true });
}

function disable() {
  root.removeAttribute('data-page-scrollbar');
  page.unmount();
  for (const scrollbar of inner.values()) scrollbar.unmount();
  inner.clear();
  additions.disconnect();
  document.removeEventListener('scroll', onScroll, { capture: true });
  document.removeEventListener('pointermove', onPointerMove);
  document.removeEventListener('pointerleave', onPointerLeave);
  window.removeEventListener('resize', onResize);
}

mouse.addEventListener('change', () => (mouse.matches ? enable() : disable()));

if (mouse.matches) enable();
